import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { avisar } from '../../shared/Notificacao';
import { Aviso, Botao, Campo } from '../../egora';
import { sessaoFoiExpirada, apagarSinalDeSessaoExpirada } from '../../casca/sessaoExpirada';
import EntradaMoldura, { EntradaCabeca, EntradaErro } from './EntradaMoldura';
import CampoSenha from './CampoSenha';
import RequisitosSenha, { senhaAtende } from './RequisitosSenha';
import RecuperarSenha from './RecuperarSenha';

/**
 * Tela de entrada do ERP (Login.dc.html): e-mail e senha, verificação em duas
 * etapas, senha do primeiro acesso e recuperação de senha.
 *
 * Diferenças da prancheta, de propósito:
 *  - não mostra o nome do escritório: antes de entrar o sistema não sabe de qual
 *    escritório a pessoa é;
 *  - "Esqueci minha senha" manda um link por e-mail (é o que o servidor faz, item 4C),
 *    e não um código de 6 números;
 *  - o código de recuperação se chama "código reserva", o mesmo nome da tela que os gera.
 */
const EMAIL_VALIDO = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Código reserva: 32 letras de a–f e números, mostrado em 8 blocos de 4 (mfa_service.py). */
function formatarCodigoReserva(texto) {
  const limpo = texto.toLowerCase().replace(/[^0-9a-f]/g, '').slice(0, 32);
  return limpo.match(/.{1,4}/g)?.join('-') || '';
}

export default function AuthScreen({ onLoginSuccess }) {
  const {
    etapaLogin, setEtapaLogin,
    erroLogin, carregando,
    login, verifyMFA, cancelarMFA, changePassword,
    usuarioLogado,
  } = useAuth();

  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erroLocal, setErroLocal] = useState('');

  const [mfaToken, setMfaToken] = useState('');
  const [usarReserva, setUsarReserva] = useState(false);
  const [erroCodigo, setErroCodigo] = useState('');

  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarNovaSenha, setConfirmarNovaSenha] = useState('');

  // Veio de uma sessão expirada? Explica uma vez e apaga o sinal.
  const [sessaoExpirou] = useState(() => sessaoFoiExpirada());
  useEffect(() => { apagarSinalDeSessaoExpirada(); }, []);

  const entrar = async (e) => {
    e.preventDefault();
    const limpo = email.trim();
    if (!EMAIL_VALIDO.test(limpo)) { setErroLocal('Confira o e-mail. Ele precisa ter o formato nome@escritorio.adv.br.'); return; }
    if (!senha) { setErroLocal('Digite sua senha.'); return; }
    setErroLocal('');
    const result = await login(limpo, senha);
    if (result.success && !result.mfaRequired && !result.firstAccess) {
      if (onLoginSuccess) onLoginSuccess();
    }
    if (result.mfaRequired) { setMfaToken(''); setUsarReserva(false); setErroCodigo(''); }
  };

  const verificar = async (e) => {
    e.preventDefault();
    const pronto = usarReserva ? mfaToken.replace(/-/g, '').length === 32 : /^\d{6}$/.test(mfaToken);
    if (!pronto) {
      setErroCodigo(usarReserva ? 'O código reserva tem 32 letras e números, em 8 blocos de 4.' : 'O código tem 6 números.');
      return;
    }
    setErroCodigo('');
    const result = await verifyMFA(mfaToken.trim());
    if (result.success && onLoginSuccess) onLoginSuccess();
  };

  const mudarCodigo = (valor) => {
    setErroCodigo('');
    setMfaToken(usarReserva ? formatarCodigoReserva(valor) : valor.replace(/\D/g, '').slice(0, 6));
  };

  const alternarReserva = () => {
    setUsarReserva((v) => !v);
    setMfaToken('');
    setErroCodigo('');
  };

  const salvarPrimeiraSenha = async (e) => {
    e.preventDefault();
    if (!senhaAtende(novaSenha)) { avisar('A senha ainda não cumpre todos os requisitos.', 'aviso'); return; }
    if (novaSenha !== confirmarNovaSenha) { avisar('As duas senhas estão diferentes.', 'aviso'); return; }
    const result = await changePassword(usuarioLogado.id, senha, novaSenha);
    if (result.success) {
      setNovaSenha(''); setConfirmarNovaSenha('');
      if (onLoginSuccess) onLoginSuccess();
    } else {
      avisar(result.error || 'Não foi possível salvar a senha. Tente de novo.', 'erro');
    }
  };

  return (
    <EntradaMoldura>
      {sessaoExpirou && etapaLogin === 'login' && (
        <Aviso tom="atencao">Sua sessão expirou. Entre de novo para continuar.</Aviso>
      )}

      {etapaLogin === 'login' && (
        <form onSubmit={entrar} noValidate>
          <EntradaCabeca titulo="Entrar" texto="Use o e-mail cadastrado pelo escritório." />
          <EntradaErro>{erroLocal || erroLogin}</EntradaErro>
          <Campo rotulo="E-mail">
            {(p) => (
              <input
                {...p}
                className="sj-campo__entrada"
                type="email"
                autoComplete="username"
                placeholder="nome@escritorio.adv.br"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErroLocal(''); }}
              />
            )}
          </Campo>
          <CampoSenha
            rotulo="Senha"
            valor={senha}
            aoMudar={(v) => { setSenha(v); setErroLocal(''); }}
            acaoDoRotulo={(
              <Botao variante="fantasma" tamanho="sm" onClick={() => setEtapaLogin('recuperacao')}>
                Esqueci minha senha
              </Botao>
            )}
          />
          <Botao type="submit" carregando={carregando} className="entrada__entrar">
            {carregando ? 'Entrando' : 'Entrar'}
          </Botao>
        </form>
      )}

      {etapaLogin === 'mfa' && (
        <form onSubmit={verificar} noValidate>
          <EntradaCabeca
            icone="phonelink_lock"
            titulo="Verificação em duas etapas"
            texto={usarReserva
              ? 'Digite um dos seus códigos reserva. Cada um serve uma vez.'
              : 'Digite o código de 6 dígitos do seu aplicativo autenticador.'}
          />
          <EntradaErro>{erroLogin}</EntradaErro>
          <Campo rotulo={usarReserva ? 'Código reserva' : 'Código'} erro={erroCodigo}>
            {(p) => (
              <input
                {...p}
                className={`sj-campo__entrada sj-num entrada__codigo${usarReserva ? ' entrada__codigo--reserva' : ''}`}
                inputMode={usarReserva ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                maxLength={39}
                value={mfaToken}
                onChange={(e) => mudarCodigo(e.target.value)}
                autoFocus
              />
            )}
          </Campo>
          <Botao variante="fantasma" tamanho="sm" icone="key" className="entrada__alternar" onClick={alternarReserva}>
            {usarReserva ? 'Usar o código do aplicativo' : 'Usar um código reserva'}
          </Botao>
          <Botao type="submit" carregando={carregando} className="entrada__entrar">
            {carregando ? 'Verificando' : 'Verificar'}
          </Botao>
          <Botao variante="fantasma" onClick={() => cancelarMFA()}>Voltar para o login</Botao>
        </form>
      )}

      {etapaLogin === 'primeiro_acesso' && (
        <form onSubmit={salvarPrimeiraSenha} noValidate>
          <EntradaCabeca
            icone="lock"
            tom="atencao"
            titulo="Crie a sua senha"
            texto="É o seu primeiro acesso. Defina a senha pessoal que você vai usar para entrar."
          />
          <CampoSenha
            rotulo="Nova senha"
            valor={novaSenha}
            aoMudar={setNovaSenha}
            autoComplete="new-password"
            autoFocus
          />
          <RequisitosSenha senha={novaSenha} />
          <CampoSenha
            rotulo="Confirme a nova senha"
            valor={confirmarNovaSenha}
            aoMudar={setConfirmarNovaSenha}
            autoComplete="new-password"
            ajuda={confirmarNovaSenha && novaSenha !== confirmarNovaSenha ? 'As duas senhas ainda estão diferentes.' : 'Digite de novo, sem colar.'}
            onPaste={(e) => { e.preventDefault(); avisar('Por segurança, digite a confirmação de novo.', 'aviso'); }}
          />
          <Botao
            type="submit"
            carregando={carregando}
            disabled={!novaSenha || novaSenha !== confirmarNovaSenha}
            className="entrada__entrar"
          >
            {carregando ? 'Salvando' : 'Salvar senha e entrar'}
          </Botao>
        </form>
      )}

      {etapaLogin === 'recuperacao' && (
        <RecuperarSenha onVoltar={() => setEtapaLogin('login')} />
      )}
    </EntradaMoldura>
  );
}
